import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { Table, Input, Alert, Button as AntButton } from "antd";

const PaginatedTable = forwardRef(
  (
    {
      columns,
      fetchData,
      rowKey = "id",
      filters = {},
      searchPlaceholder = "Search",
      searchAriaLabel,
      emptyText = "No results found",
      errorText = "Unable to load results.",
      toolbarExtra,
      tableProps = {},
    },
    ref
  ) => {
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const fetchRef = useRef(fetchData);
    useEffect(() => {
      fetchRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
      const t = setTimeout(() => setDebouncedSearch(search ?? ""), 300);
      return () => clearTimeout(t);
    }, [search]);

    const loadData = useCallback(
      async (page, pageSize, searchValue, currentFilters) => {
        setLoading(true);
        setError(null);
        try {
          const result = await fetchRef.current(
            page,
            pageSize,
            searchValue,
            currentFilters
          );
          const nextData = result?.data || [];
          const nextTotal = result?.total || 0;

          setData(nextData);
          setPagination((prev) => {
            const same =
              prev.current === page &&
              prev.pageSize === pageSize &&
              prev.total === nextTotal;
            return same ? prev : { current: page, pageSize, total: nextTotal };
          });
        } catch (nextError) {
          setError(nextError);
          setData([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
        } finally {
          setLoading(false);
        }
      },
      []
    );

    const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
    const lastFiltersKeyRef = useRef(filtersKey);
    const lastKeyRef = useRef(null);
    useEffect(() => {
      const filtersChanged = lastFiltersKeyRef.current !== filtersKey;
      if (filtersChanged) {
        lastFiltersKeyRef.current = filtersKey;
        if (pagination.current !== 1) {
          setPagination((prev) => ({ ...prev, current: 1 }));
          return;
        }
      }

      const key = `${pagination.current}|${pagination.pageSize}|${debouncedSearch}|${filtersKey}`;
      if (lastKeyRef.current === key) return;
      lastKeyRef.current = key;
      loadData(
        pagination.current,
        pagination.pageSize,
        debouncedSearch,
        filters
      );
    }, [
      debouncedSearch,
      pagination.current,
      pagination.pageSize,
      filters,
      filtersKey,
      loadData,
    ]);

    const handleTableChange = useCallback((p) => {
      setPagination((prev) => {
        if (prev.current === p.current && prev.pageSize === p.pageSize)
          return prev;
        return { ...prev, current: p.current, pageSize: p.pageSize };
      });
    }, []);

    const reloadPage = useCallback(() => {
      setError(null);
      return loadData(
        pagination.current,
        pagination.pageSize,
        debouncedSearch,
        filters
      );
    }, [loadData, pagination.current, pagination.pageSize, debouncedSearch, filters]);

    const processedColumns = useMemo(
      () =>
        columns.map(
          ({
            maxWidth = 250,
            minWidth = 100,
            onCell,
            onHeaderCell,
            ...col
          }) => {
            const baseStyle = {
              maxWidth,
              minWidth,
              whiteSpace: "normal",
              wordBreak: "break-word",
            };
            const merge =
              (fn) =>
              (...args) => {
                const cell = typeof fn === "function" ? fn(...args) : {};
                return {
                  ...cell,
                  style: { ...baseStyle, ...(cell.style || {}) },
                };
              };
            return {
              ...col,
              onCell: merge(onCell),
              onHeaderCell: merge(onHeaderCell),
            };
          }
        ),
      [columns]
    );

    useImperativeHandle(ref, () => ({ reloadPage }), [reloadPage]);

    const {
      locale: tableLocale,
      scroll: tableScroll,
      ...restTableProps
    } = tableProps;

    const paginationConfig = useMemo(
      () => ({
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) =>
          `${range[0]}–${range[1]} of ${total}`,
      }),
      [pagination]
    );

    return (
      <div className="table-card">
        <div className="table-card__toolbar">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flex: 1,
              flexWrap: "wrap",
            }}
          >
            <Input.Search
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel || searchPlaceholder}
              allowClear
              value={search}
              onChange={(e) => {
                const v = e.target.value || "";
                setPagination((prev) =>
                  prev.current === 1 ? prev : { ...prev, current: 1 }
                );
                setSearch(v);
              }}
              className="table-card__search"
            />
            {toolbarExtra}
          </div>
          <span className="table-card__count" aria-live="polite">
            {pagination.total} {pagination.total === 1 ? "result" : "results"}
          </span>
        </div>
        {error && (
          <Alert
            type="error"
            showIcon
            message={errorText}
            action={
              <AntButton type="link" size="small" onClick={reloadPage}>
                Retry
              </AntButton>
            }
            style={{ margin: 12 }}
          />
        )}
        <Table
          {...restTableProps}
          rowKey={rowKey}
          columns={processedColumns}
          dataSource={data}
          pagination={paginationConfig}
          scroll={{ x: "max-content", ...(tableScroll || {}) }}
          locale={{ emptyText, ...(tableLocale || {}) }}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    );
  }
);

PaginatedTable.displayName = "PaginatedTable";

export default PaginatedTable;
